
<script setup>
import {
    computed,
    inject,
    onBeforeUnmount,
    ref
} from 'vue';

import { useRouter } from 'vue-router';

import SidebarItem from './SidebarItem.vue';
import buildSidebarTree from '@/utils/buildRoutes';

/*
|--------------------------------------------------------------------------
| ESTADOS
|--------------------------------------------------------------------------
*/

const expandSidebar = inject('expandSidebar', ref(false));

const router = useRouter();

const menuOptions = computed(() => {
    const routes = router.getRoutes().filter(r => !!r.name);

    return buildSidebarTree(routes);
});

let hideTimeout = null;

/*
|--------------------------------------------------------------------------
| EVENTOS DEL SIDEBAR
|--------------------------------------------------------------------------
*/

const cancelarCierre = () => {
    if (hideTimeout !== null) {
        clearTimeout(hideTimeout);
        hideTimeout = null;
    }
};

const handleMouseEnter = () => {
    cancelarCierre();
};

const handleMouseLeave = () => {
    cancelarCierre();

    if (!expandSidebar.value) {
        return;
    }

    hideTimeout = setTimeout(() => {
        expandSidebar.value = false;
        hideTimeout = null;
    }, 180);
};

/*
|--------------------------------------------------------------------------
| LIMPIEZA
|--------------------------------------------------------------------------
*/

onBeforeUnmount(() => {
    cancelarCierre();
});
</script>

<template>
    <aside
        id="sidebar"
        class="navlight"
        :class="{ expand: expandSidebar }"
        @mouseenter="handleMouseEnter"
        @mouseleave="handleMouseLeave"
    >
        <div class="sidebar-wrapper">

            <ul class="sidebar-nav">

                <SidebarItem
                    v-for="(menuItem, index) in menuOptions"
                    :key="menuItem.name ?? menuItem.path ?? index"
                    :item="menuItem"
                />

            </ul>

        </div>
    </aside>
</template>

<style>
/*
|--------------------------------------------------------------------------
| SIDEBAR PRINCIPAL
|--------------------------------------------------------------------------
|
| Contraído: 40 px
| Expandido: 190 px
| Superpuesto al contenido
|
|--------------------------------------------------------------------------
*/

#sidebar {
    position: fixed;

    top: 40px;
    left: 0;

    width: 40px;
    min-width: 40px;

    height: calc(100vh - 40px);
    height: calc(100dvh - 40px);

    z-index: 1050;

    display: flex;
    flex-direction: column;

    overflow: hidden;

    transition:
        width 0.25s ease-in-out,
        min-width 0.25s ease-in-out;
}

/*
|--------------------------------------------------------------------------
| SIDEBAR EXPANDIDO
|--------------------------------------------------------------------------
*/

#sidebar.expand {
    width: 190px;
    min-width: 190px;
}

/*
|--------------------------------------------------------------------------
| SCROLL VERTICAL
|--------------------------------------------------------------------------
*/

#sidebar .sidebar-wrapper {
    flex: 1 1 auto;

    min-height: 0;
    min-width: 0;

    overflow-y: auto;
    overflow-x: hidden;

    padding-bottom: 80px;

    -webkit-overflow-scrolling: touch;
    overscroll-behavior-y: contain;

    scrollbar-width: none;
    -ms-overflow-style: none;
}

#sidebar .sidebar-wrapper::-webkit-scrollbar {
    display: none;
}

/*
|--------------------------------------------------------------------------
| NAVEGACIÓN
|--------------------------------------------------------------------------
*/

#sidebar .sidebar-nav {
    padding: 1rem 0;
    margin: 0;

    list-style: none;

    overflow: visible;
    max-height: none;
}

/*
|--------------------------------------------------------------------------
| ENLACES
|--------------------------------------------------------------------------
*/

#sidebar a.sidebar-link,
#sidebar button.sidebar-link {
    display: flex;
    align-items: center;

    width: 100%;
    min-height: 38px;

    padding: 0.5rem 0.65rem;

    font-size: 10pt;
    text-decoration: none;
    white-space: nowrap;

    border-left: 3px solid transparent;

    transition: background-color 0.2s ease;
}

#sidebar .sidebar-link:hover {
    background-color: var(--bs-danger-bg-subtle);
}

/*
|--------------------------------------------------------------------------
| ÍCONOS
|--------------------------------------------------------------------------
*/

#sidebar .sidebar-link > i:first-child {
    flex: 0 0 18px;
    width: 18px;

    text-align: center;
}

/*
|--------------------------------------------------------------------------
| SIDEBAR CONTRAÍDO
|--------------------------------------------------------------------------
*/

#sidebar:not(.expand) .sidebar-link {
    justify-content: center;

    padding: 0.65rem 0;

    text-align: center;
}

#sidebar:not(.expand) .sidebar-link > i:first-child {
    flex: 0 0 auto;
    width: 100%;

    margin: 0 !important;
    text-align: center;
}

#sidebar:not(.expand) .sidebar-link span,
#sidebar:not(.expand) .sidebar-chevron {
    display: none;
}

/*
|--------------------------------------------------------------------------
| SUBMENÚS CONTRAÍDOS
|--------------------------------------------------------------------------
*/

#sidebar:not(.expand) .sidebar-dropdown {
    display: none !important;
}

/*
|--------------------------------------------------------------------------
| SIDEBAR EXPANDIDO
|--------------------------------------------------------------------------
*/

#sidebar.expand .sidebar-link {
    justify-content: flex-start;
    text-align: left;
}

#sidebar.expand .sidebar-link span {
    display: inline;
    overflow: hidden;
    text-overflow: ellipsis;
}

/*
|--------------------------------------------------------------------------
| NIVELES DE SUBMENÚ
|--------------------------------------------------------------------------
*/

#sidebar.expand .submenu2 {
    padding-left: 15px !important;
}

#sidebar.expand .submenu3 {
    padding-left: 20px !important;
}

/*
|--------------------------------------------------------------------------
| SUBMENÚS
|--------------------------------------------------------------------------
*/

#sidebar.expand .sidebar-dropdown {
    background-color: var(--bs-tertiary-bg);
    padding: 0;
    margin: 0;
}

/*
|--------------------------------------------------------------------------
| INDICADOR DE DESPLIEGUE
|--------------------------------------------------------------------------
*/

#sidebar.expand .sidebar-chevron {
    margin-left: auto;

    font-size: 0.7rem;

    transition: transform 0.2s ease;
}

#sidebar.expand .sidebar-chevron-open {
    transform: rotate(180deg);
}

/*
|--------------------------------------------------------------------------
| TABLET / IPAD
|--------------------------------------------------------------------------
*/

@media (max-width: 1199.98px) {

    #sidebar {
        width: 40px;
        min-width: 40px;

        height: calc(100dvh - 40px);
    }

    #sidebar.expand {
        width: 190px;
        min-width: 190px;
    }

    #sidebar .sidebar-wrapper {
        -webkit-overflow-scrolling: touch;
    }
}

/*
|--------------------------------------------------------------------------
| ACCESIBILIDAD
|--------------------------------------------------------------------------
*/

@media (prefers-reduced-motion: reduce) {

    #sidebar {
        transition: none;
    }

    #sidebar .sidebar-link {
        transition: none;
    }
}

/*
|--------------------------------------------------------------------------
| FONDO SÓLIDO DEL SIDEBAR
|--------------------------------------------------------------------------
*/

#sidebar {
    background-color: var(--bs-body-bg) !important;
    opacity: 1;
    isolation: isolate;
    z-index: 1050;

    border-right: 1px solid var(--bs-border-color);
}

/*
|--------------------------------------------------------------------------
| SIDEBAR EXPANDIDO
|--------------------------------------------------------------------------
*/

#sidebar.expand {
    background-color: var(--bs-body-bg) !important;

    box-shadow: 4px 0 16px rgba(0, 0, 0, 0.16);

    border-right: 1px solid var(--bs-border-color);
}

/*
|--------------------------------------------------------------------------
| FONDO DE LOS SUBMENÚS
|--------------------------------------------------------------------------
*/

#sidebar.expand .sidebar-dropdown {
    background-color: var(--bs-body-bg) !important;
}

/*
|--------------------------------------------------------------------------
| ENLACES
|--------------------------------------------------------------------------
*/

#sidebar .sidebar-link {
    background-color: transparent;
}

#sidebar .sidebar-link:hover {
    background-color: var(--bs-tertiary-bg);
}
</style>
